import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { getTenants, getUsers } from '../services/api';
import { Tenant, User } from '../types';
import { 
  Users, Building, Building2, AlertTriangle, Plus, ChevronRight, ArrowUpRight, 
  Activity, BarChart3, Shield, Settings, Layers, PieChart, Calendar, Clock
} from 'lucide-react';
import { getTenantLevelLabel } from '../utils/format';

const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const { canCreateUsers } = usePermissions();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'tenants' | 'users'>('tenants');
  const statsRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const fetchDashboardData = async () => {
      setIsLoading(true);
      
      try {
        // Fetch tenants based on user's tenant level
        const tenantsData = await getTenants();
        setTenants(tenantsData);
        
        // Fetch users based on user's tenant level
        let userParams = {};
        if (user?.tenant_level === 'L3') {
          // Only fetch users for the current tenant
          userParams = { tenant_id: user.tenant_id };
        } else if (user?.tenant_level === 'L0' && user?.role?.toLowerCase() === 'admin') {
          // L0 admins should see all users from all tenant levels
          userParams = {};
        }
        // For L1, L2 users, let the backend handle the hierarchy-based filtering
        
        const usersData = await getUsers(userParams);
        setUsers(usersData);
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    };
    
    if (user) {
      fetchDashboardData();
    }
  }, [user]);
  
  // Calculate statistics
  const stats = {
    totalTenants: tenants.length,
    activeTenants: tenants.filter(t => t.status === 'active').length,
    inactiveTenants: tenants.filter(t => t.status === 'inactive').length,
    pendingTenants: tenants.filter(t => t.status === 'pending').length,
    totalUsers: users.length,
    activeUsers: users.filter(u => u.status === 'active').length,
    inactiveUsers: users.filter(u => u.status === 'inactive').length,
    partnerTenants: tenants.filter(t => t.level === 'L2').length,
    orgTenants: tenants.filter(t => t.level === 'L3').length,
  };
  
  // Determine which stats to show based on tenant level
  const showPartnerStats = user?.tenant_level === 'L0' || user?.tenant_level === 'L1';
  const showOrgStats = user?.tenant_level === 'L0' || user?.tenant_level === 'L1' || user?.tenant_level === 'L2';
  const canAddTenant = user?.tenant_level === 'L0' || user?.tenant_level === 'L1' || user?.tenant_level === 'L2';
  
  // Scroll to stats section on mobile
  const scrollToStats = () => {
    if (statsRef.current) {
      statsRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Get current date and time
  const currentDate = new Date();
  const formattedDate = currentDate.toLocaleDateString('en-US', { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });
  const formattedTime = currentDate.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit'
  });
  
  return (
    <div className="space-y-5 sm:space-y-6 pb-6">
      {/* Enhanced Hero section */}
      <div className="bg-gradient-to-br from-blue-900/90 via-indigo-900/80 to-blue-800/60 rounded-xl shadow-xl border border-blue-700/30 relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(99,102,241,0.1)_1px,transparent_1px)] bg-[length:24px_24px] opacity-30"></div>
        <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-400/70 via-indigo-300/70 to-blue-400/70"></div>
        <div className="absolute -top-24 -right-24 w-48 h-48 bg-blue-500/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-indigo-500/10 rounded-full blur-3xl"></div>
        
        <div className="relative z-10 p-5 sm:p-7">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 mb-4 sm:mb-6">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-200 to-indigo-200 font-display tracking-wide">Administration Dashboard</h1>
              <p className="text-blue-300/80 text-sm mt-1">System administration and analytics dashboard</p>
            </div>
            <div className="flex flex-col items-end">
              <div className="text-sm text-blue-200 font-medium">
                Welcome, {user?.first_name} {user?.last_name} 
              </div>
              <div className="text-xs text-blue-300/70 mt-1 flex items-center gap-1.5">
                <Calendar size={12} className="opacity-70" />
                <span>{formattedDate}</span>
                <span className="mx-1">•</span>
                <Clock size={12} className="opacity-70" /> 
                <span>{formattedTime}</span>
              </div>
            </div>
          </div>
          
         
          
          {/* CTA Button - visible only on mobile */}
          <div className="mt-4 block sm:hidden">
            <button
              onClick={scrollToStats}
              className="w-full bg-blue-600/30 border border-blue-500/30 text-blue-200 rounded-lg py-2.5 flex items-center justify-center space-x-2 hover:bg-blue-600/40 transition-colors"
            >
              <span>View Detailed Stats</span>
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>
      
      {/* Enhanced Count Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
        <div className="bg-gradient-to-br from-blue-900/50 to-indigo-900/30 rounded-xl shadow-lg border border-blue-700/20 p-4 sm:p-5 hover:shadow-xl transition-all duration-200 group">
          <div className="flex items-start justify-between mb-3">
            <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500/20 to-blue-600/10 border border-blue-500/20">
              <Users size={20} className="text-blue-400 group-hover:text-blue-300 transition-colors" />
            </div>
            <div className="flex items-center">
              <span className={`inline-flex items-center justify-center px-1.5 py-0.5 text-xs rounded-md ${
                stats.activeUsers > 0 ? 'bg-emerald-900/30 text-emerald-400 border border-emerald-700/30' : 'bg-amber-900/30 text-amber-400 border border-amber-700/30'
              }`}>
                {stats.activeUsers > 0 ? 'Active' : 'No Users'}
              </span>
            </div>
          </div>
          <h3 className="text-xs text-blue-300/80 font-medium uppercase tracking-wider mb-1">Total Users</h3>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl sm:text-3xl font-bold text-white">{stats.totalUsers}</span>
            <span className="text-xs text-blue-300/70">{stats.activeUsers} active ({Math.round((stats.activeUsers / (stats.totalUsers || 1)) * 100)}%)</span>
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-blue-900/50 to-purple-900/30 rounded-xl shadow-lg border border-blue-700/20 p-4 sm:p-5 hover:shadow-xl transition-all duration-200 group">
          <div className="flex items-start justify-between mb-3">
            <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500/20 to-indigo-600/10 border border-blue-500/20">
              <Building size={20} className="text-blue-400 group-hover:text-indigo-300 transition-colors" />
            </div>
            <div className="flex items-center">
              <span className={`inline-flex items-center justify-center px-1.5 py-0.5 text-xs rounded-md ${
                stats.activeTenants > 0 ? 'bg-emerald-900/30 text-emerald-400 border border-emerald-700/30' : 'bg-amber-900/30 text-amber-400 border border-amber-700/30'
              }`}>
                {stats.activeTenants > 0 ? 'Active' : 'No Tenants'}
              </span>
            </div>
          </div>
          <h3 className="text-xs text-blue-300/80 font-medium uppercase tracking-wider mb-1">Total Tenants</h3>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl sm:text-3xl font-bold text-white">{stats.totalTenants}</span>
            <span className="text-xs text-blue-300/70">{stats.activeTenants} active ({Math.round((stats.activeTenants / (stats.totalTenants || 1)) * 100)}%)</span>
          </div>
        </div>
        
        {showPartnerStats && (
          <div className="bg-gradient-to-br from-purple-900/50 to-blue-900/30 rounded-xl shadow-lg border border-purple-700/20 p-4 sm:p-5 hover:shadow-xl transition-all duration-200 group">
            <div className="flex items-start justify-between mb-3">
              <div className="p-2 rounded-lg bg-gradient-to-br from-purple-500/20 to-purple-600/10 border border-purple-500/20">
                <Building2 size={20} className="text-purple-400 group-hover:text-purple-300 transition-colors" />
              </div>
              <div className="flex items-center">
                <span className={`inline-flex items-center justify-center px-1.5 py-0.5 text-xs rounded-md ${
                  stats.partnerTenants > 0 ? 'bg-emerald-900/30 text-emerald-400 border border-emerald-700/30' : 'bg-amber-900/30 text-amber-400 border border-amber-700/30'
                }`}>
                  {stats.partnerTenants > 0 ? 'Active' : 'No Partners'}
                </span>
              </div>
            </div>
            <h3 className="text-xs text-purple-300/80 font-medium uppercase tracking-wider mb-1">Partners (L2)</h3>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl sm:text-3xl font-bold text-white">{stats.partnerTenants}</span>
              <span className="text-xs text-purple-300/70">Level 2 tenants</span>
            </div>
          </div>
        )}
        
        {showOrgStats && (
          <div className="bg-gradient-to-br from-cyan-900/50 to-blue-900/30 rounded-xl shadow-lg border border-cyan-700/20 p-4 sm:p-5 hover:shadow-xl transition-all duration-200 group">
            <div className="flex items-start justify-between mb-3">
              <div className="p-2 rounded-lg bg-gradient-to-br from-cyan-500/20 to-cyan-600/10 border border-cyan-500/20">
                <Building size={20} className="text-cyan-400 group-hover:text-cyan-300 transition-colors" />
              </div>
              <div className="flex items-center">
                <span className={`inline-flex items-center justify-center px-1.5 py-0.5 text-xs rounded-md ${
                  stats.orgTenants > 0 ? 'bg-emerald-900/30 text-emerald-400 border border-emerald-700/30' : 'bg-amber-900/30 text-amber-400 border border-amber-700/30'
                }`}>
                  {stats.orgTenants > 0 ? 'Active' : 'No Orgs'}
                </span>
              </div>
            </div>
            <h3 className="text-xs text-cyan-300/80 font-medium uppercase tracking-wider mb-1">Customers (L3)</h3>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl sm:text-3xl font-bold text-white">{stats.orgTenants}</span>
              <span className="text-xs text-cyan-300/70">Level 3 tenants</span>
            </div>
          </div>
        )}
      </div>
      
      {/* Enhanced Quick Action Buttons */}
      {canAddTenant && (
        <div className="bg-gradient-to-br from-gray-900/80 to-gray-900/40 rounded-xl border border-gray-800/30 p-5 sm:p-6">
          <h2 className="text-lg font-medium text-white mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          <Link to="/tenants/new" className="block">
              <div className="bg-gradient-to-br from-green-900/20 to-green-900/5 hover:from-green-900/30 hover:to-green-900/10 rounded-xl border border-green-700/20 hover:border-green-600/30 p-4 sm:p-5 shadow-md hover:shadow-lg transition-all duration-200 h-full">
              <div className="flex items-center">
                  <div className="p-2.5 sm:p-3 rounded-lg bg-gradient-to-b from-green-800/40 to-green-900/20 border border-green-600/30 mr-3 sm:mr-4 shadow-md">
                  <Building size={20} className="text-green-400" />
              </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-white text-sm sm:text-base truncate">Add New Tenant</h3>
                  <p className="text-xs sm:text-sm text-gray-400 truncate">
                    Create a new tenant
                  </p>
                </div>
                <div className="p-1.5 sm:p-2 rounded-full bg-gradient-to-b from-green-800/40 to-green-900/20 border border-green-600/30 shadow-md ml-2">
                  <Plus size={14} className="text-green-400" />
                </div>
              </div>
            </div>
          </Link>
          
          {showPartnerStats && (
            <Link to="/tenants/new?level=L2&type=partner" className="block">
                <div className="bg-gradient-to-br from-indigo-900/20 to-indigo-900/5 hover:from-indigo-900/30 hover:to-indigo-900/10 rounded-xl border border-indigo-700/20 hover:border-indigo-600/30 p-4 sm:p-5 shadow-md hover:shadow-lg transition-all duration-200 h-full">
                <div className="flex items-center">
                    <div className="p-2.5 sm:p-3 rounded-lg bg-gradient-to-b from-indigo-800/40 to-indigo-900/20 border border-indigo-600/30 mr-3 sm:mr-4 shadow-md">
                    <Building2 size={20} className="text-indigo-400" />
                </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-white text-sm sm:text-base truncate">Add New Partner</h3>
                    <p className="text-xs sm:text-sm text-gray-400 truncate">
                      Create L2 partner
                    </p>
                  </div>
                  <div className="p-1.5 sm:p-2 rounded-full bg-gradient-to-b from-indigo-800/40 to-indigo-900/20 border border-indigo-600/30 shadow-md ml-2">
                    <Plus size={14} className="text-indigo-400" />
                  </div>
                </div>
              </div>
            </Link>
          )}
          
          {showOrgStats && (
            <Link to="/tenants/new?level=L3&type=org" className="block">
                <div className="bg-gradient-to-br from-blue-900/20 to-blue-900/5 hover:from-blue-900/30 hover:to-blue-900/10 rounded-xl border border-blue-700/20 hover:border-blue-600/30 p-4 sm:p-5 shadow-md hover:shadow-lg transition-all duration-200 h-full">
                <div className="flex items-center">
                    <div className="p-2.5 sm:p-3 rounded-lg bg-gradient-to-b from-blue-800/40 to-blue-900/20 border border-blue-600/30 mr-3 sm:mr-4 shadow-md">
                    <Building size={20} className="text-blue-400" />
                </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-white text-sm sm:text-base truncate">Add New Organization</h3>
                    <p className="text-xs sm:text-sm text-gray-400 truncate">
                      Create L3 organization
                    </p>
                  </div>
                  <div className="p-1.5 sm:p-2 rounded-full bg-gradient-to-b from-blue-800/40 to-blue-900/20 border border-blue-600/30 shadow-md ml-2">
                    <Plus size={14} className="text-blue-400" />
                  </div>
                </div>
              </div>
            </Link>
          )}
          </div>
        </div>
      )}
      
      {/* Enhanced Loading Indicator */}
      {isLoading ? (
        <div className="flex justify-center items-center py-10 sm:py-12">
          <div className="flex flex-col items-center">
          <div className="w-14 h-14 sm:w-16 sm:h-16 relative">
            <div className="absolute top-0 right-0 h-full w-full rounded-full border-4 border-t-blue-500 border-r-transparent border-b-transparent border-l-transparent animate-spin"></div>
            <div className="absolute top-1 right-1 h-12 w-12 sm:h-14 sm:w-14 rounded-full border-4 border-t-transparent border-r-indigo-500 border-b-transparent border-l-transparent animate-spin-slow"></div>
            </div>
            <p className="text-blue-300/80 mt-4 text-sm">Loading dashboard data...</p>
          </div>
        </div>
      ) : (
        <>
          {/* Mobile tab switcher */}
          <div className="sm:hidden flex rounded-lg bg-gray-900/50 p-1 mb-4" ref={statsRef}>
            <button
              className={`flex-1 py-2 rounded-md text-sm font-medium ${
                activeTab === 'tenants' 
                  ? 'bg-blue-900/70 text-blue-200 shadow-sm' 
                  : 'text-gray-400 hover:text-gray-300'
              }`}
              onClick={() => setActiveTab('tenants')}
            >
              Tenants
            </button>
            <button
              className={`flex-1 py-2 rounded-md text-sm font-medium ${
                activeTab === 'users' 
                  ? 'bg-blue-900/70 text-blue-200 shadow-sm' 
                  : 'text-gray-400 hover:text-gray-300'
              }`}
              onClick={() => setActiveTab('users')}
            >
              Users
            </button>
          </div>
          
          {/* Enhanced Detailed Stats Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 sm:gap-6">
            {/* Recent Tenants - Enhanced */}
            <div className={`bg-gradient-to-br from-gray-900/80 to-gray-900/40 rounded-xl shadow-lg border border-gray-800/40 overflow-hidden ${
              activeTab === 'users' ? 'hidden sm:block' : ''
            }`}>
              <div className="p-5 sm:p-6">
                <div className="flex justify-between items-center mb-4">
                  <div className="flex items-center gap-2">
                    <Building size={18} className="text-indigo-400" />
                    <h3 className="text-lg sm:text-xl font-medium text-white">Recent Tenants</h3>
                  </div>
                {canAddTenant && (
                  <Link to="/tenants/new">
                      <button className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-gradient-to-br from-indigo-900/30 to-indigo-900/10 border border-indigo-700/30 text-indigo-300 hover:text-indigo-200 hover:border-indigo-600/40 shadow-sm hover:shadow-md transition-all duration-200">
                      <Plus size={14} />
                      <span className="text-xs sm:text-sm">Add</span>
                    </button>
                  </Link>
                )}
              </div>
              
              {tenants.length > 0 ? (
                  <div className="overflow-x-auto -mx-5 sm:-mx-6">
                  <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b border-gray-800/60">
                          <th className="px-5 sm:px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Name</th>
                          <th className="px-5 sm:px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Level</th>
                          <th className="px-5 sm:px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Status</th>
                      </tr>
                    </thead>
                      <tbody className="divide-y divide-gray-800/40">
                      {tenants.slice(0, 5).map((tenant, index) => (
                        <tr key={tenant.id} 
                            className="hover:bg-blue-900/10 transition-colors duration-150 animate-fadeIn"
                          style={{ animationDelay: `${index * 50}ms` }}
                        >
                            <td className="px-5 sm:px-6 py-3.5 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-700/20 to-indigo-600/10 border border-indigo-600/30 flex items-center justify-center mr-3">
                                  <span className="text-xs font-medium text-indigo-300">{tenant.name.charAt(0).toUpperCase()}</span>
                                </div>
                                <span className="text-white font-medium">{tenant.name}</span>
                              </div>
                            </td>
                            <td className="px-5 sm:px-6 py-3.5 whitespace-nowrap">
                              <div className="flex items-center">
                                <span className={`inline-flex items-center justify-center px-2 py-1 rounded text-xs font-medium ${
                                  tenant.level === 'L0' ? 'bg-violet-900/30 text-violet-400 border border-violet-700/30' :
                                  tenant.level === 'L1' ? 'bg-cyan-900/30 text-cyan-400 border border-cyan-700/30' :
                                  tenant.level === 'L2' ? 'bg-indigo-900/30 text-indigo-400 border border-indigo-700/30' :
                                  'bg-blue-900/30 text-blue-400 border border-blue-700/30'
                                }`}>
                                  {tenant.level}
                                </span>
                              </div>
                            </td>
                            <td className="px-5 sm:px-6 py-3.5 whitespace-nowrap">
                            <span className={`
                                inline-flex items-center justify-center px-2 py-1 rounded text-xs font-medium
                              ${tenant.status === 'active' 
                                  ? 'bg-emerald-900/30 text-emerald-400 border border-emerald-700/30' 
                                : tenant.status === 'inactive' 
                                  ? 'bg-red-900/30 text-red-400 border border-red-700/30' 
                                  : 'bg-amber-900/30 text-amber-400 border border-amber-700/30'}
                            `}>
                              {tenant.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                  <div className="text-center py-6 sm:py-8 bg-indigo-900/10 rounded-lg border border-indigo-800/20">
                    <Building size={28} className="text-indigo-400/60 mx-auto mb-3" />
                  <p className="text-gray-400 mb-4 text-sm">
                    No tenants found
                  </p>
                  {canAddTenant && (
                    <Link to="/tenants/new">
                      <button 
                          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-b from-indigo-700 to-indigo-800 hover:from-indigo-600 hover:to-indigo-700 text-white shadow-md hover:shadow-indigo-500/20 border border-indigo-500/20 transition-all duration-200 mx-auto"
                      >
                        <Plus size={16} />
                        <span className="text-sm">Add Your First Tenant</span>
                      </button>
                    </Link>
                  )}
                </div>
              )}
              
              {tenants.length > 0 && (
                  <div className="mt-4 text-right pt-2 border-t border-gray-800/30">
                    <Link to="/tenants" className="inline-flex items-center text-indigo-400 hover:text-indigo-300 text-sm px-3 py-2 -mr-3 rounded-md hover:bg-indigo-900/20 transition-colors">
                    <span>View all tenants</span>
                      <ArrowUpRight size={16} className="ml-1" />
                  </Link>
                </div>
              )}
              </div>
            </div>
            
            {/* Recent Users - Enhanced */}
            <div className={`bg-gradient-to-br from-gray-900/80 to-gray-900/40 rounded-xl shadow-lg border border-gray-800/40 overflow-hidden ${
              activeTab === 'tenants' ? 'hidden sm:block' : ''
            }`}>
              <div className="p-5 sm:p-6">
                <div className="flex justify-between items-center mb-4">
                  <div className="flex items-center gap-2">
                    <Users size={18} className="text-blue-400" />
                    <h3 className="text-lg sm:text-xl font-medium text-white">Recent Users</h3>
                  </div>
                {canCreateUsers() && (
                  <Link to="/users/new">
                      <button className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-gradient-to-br from-blue-900/30 to-blue-900/10 border border-blue-700/30 text-blue-300 hover:text-blue-200 hover:border-blue-600/40 shadow-sm hover:shadow-md transition-all duration-200">
                      <Plus size={14} />
                      <span className="text-xs sm:text-sm">Add</span>
                    </button>
                  </Link>
                )}
              </div>
              
              {users.length > 0 ? (
                  <div className="overflow-x-auto -mx-5 sm:-mx-6">
                  <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b border-gray-800/60">
                          <th className="px-5 sm:px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Username</th>
                          <th className="px-5 sm:px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Email</th>
                          <th className="px-5 sm:px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Role</th>
                      </tr>
                    </thead>
                      <tbody className="divide-y divide-gray-800/40">
                      {users.slice(0, 5).map((user, index) => (
                        <tr key={user.id} 
                            className="hover:bg-blue-900/10 transition-colors duration-150 animate-fadeIn"
                          style={{ animationDelay: `${index * 50}ms` }}
                        >
                            <td className="px-5 sm:px-6 py-3.5 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-700/20 to-blue-600/10 border border-blue-600/30 flex items-center justify-center mr-3">
                                  <span className="text-xs font-medium text-blue-300">{user.username.charAt(0).toUpperCase()}</span>
                                </div>
                                <span className="text-white font-medium">{user.username}</span>
                              </div>
                            </td>
                            <td className="px-5 sm:px-6 py-3.5 whitespace-nowrap text-gray-300 overflow-hidden text-ellipsis max-w-[180px]">
                              {user.email}
                            </td>
                            <td className="px-5 sm:px-6 py-3.5 whitespace-nowrap">
                            <span className={`
                                inline-flex items-center justify-center px-2 py-1 rounded text-xs font-medium
                              ${user.role?.toLowerCase() === 'admin' 
                                  ? 'bg-indigo-900/30 text-indigo-400 border border-indigo-700/30' 
                                : user.role === 'manager' 
                                  ? 'bg-blue-900/30 text-blue-400 border border-blue-700/30' 
                                  : 'bg-gray-900/30 text-gray-400 border border-gray-700/30'}
                            `}>
                              {user.role}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                  <div className="text-center py-6 sm:py-8 bg-blue-900/10 rounded-lg border border-blue-800/20">
                    <Users size={28} className="text-blue-400/60 mx-auto mb-3" />
                  <p className="text-gray-400 mb-4 text-sm">
                    No users found
                  </p>
                  <Link to="/users/new">
                    <button 
                        className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-b from-blue-700 to-blue-800 hover:from-blue-600 hover:to-blue-700 text-white shadow-md hover:shadow-blue-500/20 border border-blue-500/20 transition-all duration-200 mx-auto"
                    >
                      <Plus size={16} />
                      <span className="text-sm">Add Your First User</span>
                    </button>
                  </Link>
                </div>
              )}
              
              {users.length > 0 && (
                  <div className="mt-4 text-right pt-2 border-t border-gray-800/30">
                    <Link to="/tenants?view=users" className="inline-flex items-center text-blue-400 hover:text-blue-300 text-sm px-3 py-2 -mr-3 rounded-md hover:bg-blue-900/20 transition-colors">
                      <span>View all users</span>
                      <ArrowUpRight size={16} className="ml-1" />
                    </Link>
                  </div>
              )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Add CSS animation class
const style = document.createElement('style');
style.textContent = `
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.7; }
  }
  .pulse-animation {
    animation: pulse 2s infinite;
  }
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .animate-fadeIn {
    animation: fadeIn 0.3s ease-out forwards;
  }
`;
document.head.appendChild(style);

// Export the component
export default DashboardPage; 